import { SceneManager } from '../scenes/SceneManager';
import { showCursorSpinner, hideCursorSpinner } from '../utils/cursorSpinner';

export class MaterialSelector {
  private sceneManager: SceneManager;
  private presetsContainer: HTMLElement;
  private currentMaterial: string | null = null;
  private presetsById: Record<string, any> = {};
  private isLoaded: boolean = false;
  private isLoading: boolean = false;
  private loadingPromise: Promise<void> | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private hoverPreview: HTMLElement | null = null;

  constructor(sceneManager: SceneManager) {
    this.sceneManager = sceneManager;
    this.presetsContainer = document.getElementById('material-grid')!;
    
    // Создаем элемент для увеличенного превью при наведении
    this.createHoverPreview();
    
    // НЕ загружаем пресеты сразу — только когда откроют панель
    // this.loadPresets();

    // Обновляем превью материала при загрузке проекта / смене слоя по сохранённому presetId
    window.addEventListener('materialPresetSelect', ((e: CustomEvent) => {
      const presetId = e.detail?.id as string | null;
      this.updatePreviewByPresetId(presetId);
    }) as EventListener);

    // Слушаем событие сброса материала
    window.addEventListener('resetMaterial', () => {
      this.resetMaterialPreview();
    });

    // Слушаем событие открытия панели материалов
    window.addEventListener('materialPanelOpened', async () => {
      await this.ensureLoaded();
    });
    
    // Скрываем превью при закрытии панели
    const materialPanel = document.getElementById('material-selector-panel');
    const closeBtn = document.getElementById('close-material-selector');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hideHoverPreview();
      });
    }
    
    // Также скрываем при клике вне панели
    document.addEventListener('click', (e) => {
      if (materialPanel && !materialPanel.contains(e.target as Node) && !materialPanel.classList.contains('hidden')) {
        // Панель может быть закрыта, скрываем превью
        this.hideHoverPreview();
      }
    });

    // Инициализируем Intersection Observer для lazy loading превьюшек
    this.initIntersectionObserver();
  }

  private createHoverPreview(): void {
    // Если элемент уже существует, не создаем заново
    if (this.hoverPreview && document.body.contains(this.hoverPreview)) {
      return;
    }
    
    // Добавляем элемент в body, чтобы он был поверх всего
    this.hoverPreview = document.createElement('div');
    this.hoverPreview.className = 'material-hover-preview';
    document.body.appendChild(this.hoverPreview);
  }

  // Публичный метод для загрузки пресетов (вызывается при открытии панели)
  public async ensureLoaded(): Promise<void> {
    // Если уже загружено - возвращаемся сразу
    if (this.isLoaded) {
      return;
    }
    
    // Если загрузка в процессе - ждем завершения существующей загрузки
    if (this.isLoading && this.loadingPromise) {
      return this.loadingPromise;
    }
    
    // Начинаем новую загрузку
    this.isLoading = true;
    this.loadingPromise = this.loadPresets()
      .then(() => {
        this.isLoaded = true;
      })
      .finally(() => {
        this.isLoading = false;
        this.loadingPromise = null;
      });
    
    return this.loadingPromise;
  }

  private getMaterialCategory(preset: any): string {
    const id = (preset.id || '').toLowerCase();
    const name = (preset.name || '').toLowerCase();
    const combined = `${id} ${name}`;
    
    // None - особая категория
    if (id === 'none') {
      return 'none';
    }
    
    // Fabric - все ткани
    if (combined.includes('fabric') || combined.includes('cotton') || 
        combined.includes('canvas') || combined.includes('knitted') || 
        combined.includes('knited') || combined.includes('mesh') || 
        combined.includes('lace') || combined.includes('rug') || 
        combined.includes('tarp') || combined.includes('polyester') || 
        combined.includes('nylon') || combined.includes('nonwoven') || 
        combined.includes('camo') || combined.includes('glitter') || 
        combined.includes('ribbon') || combined.includes('woven') ||
        combined.includes('twill') || id === 'wood_oak') {
      return 'fabric';
    }
    
    // Metal
    if (combined.includes('metal') || combined.includes('chainmail')) {
      return 'metal';
    }
    
    // Leather
    if (combined.includes('leather')) {
      return 'leather';
    }
    
    // Wood (исключаем wood_oak, так как он теперь ткань)
    if ((combined.includes('wood') || combined.includes('oak')) && id !== 'wood_oak') {
      return 'wood';
    }
    
    // Ground/Other
    if (combined.includes('ground')) {
      return 'ground';
    }
    
    return 'other';
  }

  private async loadPresets(): Promise<void> {
    try {
      const response = await fetch('/presets/index.json');
      const presets = await response.json();
      
      // Очищаем контейнер
      this.presetsContainer.innerHTML = '';
      
      // Группируем материалы по категориям
      const categories: Record<string, any[]> = {};
      
      for (const preset of presets) {
        // Пропускаем пресет "none" - для него используется только кнопка сброса
        if (preset.id === 'none') {
          continue;
        }
        // Пресет "empty-m" используем только для кнопки сброса, в сетку не добавляем
        if (preset.id === 'empty-m') {
          this.presetsById[preset.id] = preset;
          continue;
        }
        
        const category = this.getMaterialCategory(preset);
        if (!categories[category]) {
          categories[category] = [];
        }
        categories[category].push(preset);
      }
      
      // Всегда добавляем категорию "none" для кнопки сброса
      if (!categories['none']) {
        categories['none'] = [];
      }
      
      // Порядок отображения категорий
      const categoryOrder = ['none', 'fabric', 'metal', 'leather', 'wood', 'ground', 'other'];
      const categoryNames: Record<string, string> = {
        'none': 'Без материала',
        'fabric': 'Ткани',
        'metal': 'Металлы',
        'leather': 'Кожа',
        'wood': 'Дерево',
        'ground': 'Поверхности',
        'other': 'Прочее'
      };
      
      // Добавляем группы по порядку
      for (const categoryKey of categoryOrder) {
        if (categories[categoryKey] && categories[categoryKey].length >= 0) {
          const itemCount = categoryKey === 'none' ? 1 : categories[categoryKey].length;
          const groupContainer = this.createCategoryGroup(
            categoryNames[categoryKey], 
            categoryKey,
            itemCount
          );
          
          // Для категории "none" добавляем только кнопку сброса, не добавляем сам пресет "none"
          if (categoryKey === 'none') {
            // Кнопка сброса уже добавлена в createCategoryGroup
          } else {
            // Для остальных категорий добавляем все пресеты
            for (const preset of categories[categoryKey]) {
              await this.addPresetOption(preset, groupContainer);
            }
          }
          
          this.presetsContainer.appendChild(groupContainer);
        }
      }
      
      // Подключаем Intersection Observer для lazy loading
      const cards = this.presetsContainer.querySelectorAll('.lazy-thumbnail');
      cards.forEach(card => {
        this.intersectionObserver?.observe(card);
      });
    } catch (error) {
      console.error('Ошибка загрузки пресетов материалов:', error);
    }
  }

  private createCategoryGroup(title: string, categoryKey: string, itemCount: number = 0): HTMLElement {
    const groupContainer = document.createElement('div');
    groupContainer.className = 'material-category-group';
    groupContainer.dataset.category = categoryKey;
    
    const groupHeader = document.createElement('div');
    groupHeader.className = 'material-category-header';
    const countText = itemCount > 0 ? ` <span class="material-category-count">(${itemCount})</span>` : '';
    groupHeader.innerHTML = `
      <span class="material-category-title">${title}${countText}</span>
      <span class="material-category-toggle">▼</span>
    `;
    
    const groupContent = document.createElement('div');
    groupContent.className = 'material-category-content';
    
    // Для категории "none" сразу добавляем кнопку сброса
    if (categoryKey === 'none') {
      const resetWrapper = document.createElement('div');
      resetWrapper.className = 'material-category-items';
      this.addResetButton(resetWrapper);
      groupContent.appendChild(resetWrapper);
    } else {
      const itemsWrapper = document.createElement('div');
      itemsWrapper.className = 'material-category-items';
      groupContent.appendChild(itemsWrapper);
    }
    
    // Обработчик сворачивания/разворачивания
    let isExpanded = true;
    groupHeader.addEventListener('click', () => {
      isExpanded = !isExpanded;
      if (isExpanded) {
        groupContent.style.display = 'block';
        groupHeader.querySelector('.material-category-toggle')!.textContent = '▼';
      } else {
        groupContent.style.display = 'none';
        groupHeader.querySelector('.material-category-toggle')!.textContent = '▶';
      }
    });
    
    groupContainer.appendChild(groupHeader);
    groupContainer.appendChild(groupContent);
    
    return groupContainer;
  }

  private addResetButton(container?: HTMLElement): void {
    const targetContainer = container || this.presetsContainer;
    
    const wrapper = document.createElement('div');
    wrapper.className = 'material-item-wrapper';
    
    const resetCard = document.createElement('div');
    resetCard.className = 'preset-card reset-card';
    resetCard.id = 'reset-material-btn';
    resetCard.title = 'Сбросить материал';
    resetCard.innerHTML = '<span class="reset-icon">↺</span>';
    
    const label = document.createElement('div');
    label.className = 'material-item-label';
    label.textContent = 'Сброс';
    
    resetCard.addEventListener('click', async (e: MouseEvent) => {
      const preset = this.presetsById['empty-m'];
      if (preset) {
        this.selectMaterial('empty-m', preset.name || 'Без материала');
        const thumbnailUrl = preset.thumbnail ? `/presets/${preset.thumbnail}` : `/presets/${preset.path}empty-m.png`;
        if (thumbnailUrl) {
          this.showHoverPreview(thumbnailUrl, preset.name || 'Без материала', resetCard);
        }
        showCursorSpinner(e.clientX, e.clientY);
        try {
          await this.sceneManager.applyServerPreset(preset);
          this.updateMaterialPreview(preset);
        } finally {
          hideCursorSpinner();
        }
      } else {
        window.dispatchEvent(new CustomEvent('resetMaterial'));
        this.resetMaterialPreview();
      }
    });
    
    wrapper.appendChild(resetCard);
    wrapper.appendChild(label);
    targetContainer.appendChild(wrapper);
  }


  private async addPresetOption(preset: any, container?: HTMLElement): Promise<void> {
    // Определяем контейнер для добавления
    let targetContainer: HTMLElement;
    if (container) {
      // Если передан контейнер группы, ищем внутри него .material-category-items
      const itemsWrapper = container.querySelector('.material-category-items');
      targetContainer = (itemsWrapper as HTMLElement) || container;
    } else {
      targetContainer = this.presetsContainer;
    }
    
    const wrapper = document.createElement('div');
    wrapper.className = 'material-item-wrapper';
    
    const card = document.createElement('div');
    card.className = 'preset-card';
    card.title = preset.name || preset.id || '';
    
    // Кэшируем пресет по id, чтобы можно было восстановить превью по сохраненному presetId
    if (preset.id) {
      this.presetsById[preset.id] = preset;
    }

    // Определяем URL превьюшки
    let thumbnailUrl = preset.thumbnail ? `/presets/${preset.thumbnail}` : null;
    
    if (!thumbnailUrl && preset.path) {
      // Пробуем normal.jpg как fallback (без fetch HEAD — просто предполагаем что он есть)
      thumbnailUrl = `/presets/${preset.path}normal.jpg`;
    }
    
    // Если ничего не нашлось, используем дефолтный SVG
    if (!thumbnailUrl) {
      thumbnailUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0iIzk5OSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiMxYTFhMWEiIHJ4PSIzMiIvPjxwYXRoIGQ9Ik0yMCAzM2gxNEMzNyAzMyAzOSAzNSAzOSAzOHMtMiA1LTUgNUgyMGMtMyAwLTUtMi01LTVzMi01IDUtNXoiIGZpbGw9IiMyYTJhMmEiLz48L3N2Zz4=';
    }
    
    // Используем data-атрибут для lazy loading
    card.setAttribute('data-thumbnail', thumbnailUrl);
    card.classList.add('lazy-thumbnail');
    
    // Показываем placeholder пока не загрузилось
    card.style.backgroundColor = '#1a1a1a';
    
    const label = document.createElement('div');
    label.className = 'material-item-label';
    label.textContent = preset.name || preset.id || 'Material';
    
    // Обработчики для увеличенного превью при наведении
    const showPreview = () => {
      if (thumbnailUrl) {
        this.showHoverPreview(thumbnailUrl, preset.name || preset.id || 'Material', card);
      }
    };
    
    const hidePreview = () => {
      this.hideHoverPreview();
    };
    
    card.addEventListener('mouseenter', showPreview);
    card.addEventListener('mouseleave', hidePreview);
    wrapper.addEventListener('mouseenter', showPreview);
    wrapper.addEventListener('mouseleave', hidePreview);
    
    card.addEventListener('click', async (e: MouseEvent) => {
      this.selectMaterial(preset.id, preset.name || preset.id);
      
      // Обновляем превью при клике, но не скрываем его
      if (thumbnailUrl) {
        this.showHoverPreview(thumbnailUrl, preset.name || preset.id || 'Material', card);
      }
      
      // Показываем spinner возле курсора
      showCursorSpinner(e.clientX, e.clientY);
      
      try {
        await this.sceneManager.applyServerPreset(preset);
        this.updateMaterialPreview(preset);
      } finally {
        // Скрываем spinner
        hideCursorSpinner();
      }
      // НЕ закрываем панель при выборе материала
    });
    
    wrapper.appendChild(card);
    wrapper.appendChild(label);
    targetContainer.appendChild(wrapper);
  }

  private showHoverPreview(thumbnailUrl: string, name: string, sourceCard: HTMLElement): void {
    if (!this.hoverPreview) {
      // Если элемент не создан, создаем его
      this.createHoverPreview();
      if (!this.hoverPreview) return;
    }
    
    // Позиционируем под кнопкой "Файл" слева сверху
    const fileBtn = document.getElementById('file-btn');
    if (fileBtn) {
      const fileBtnRect = fileBtn.getBoundingClientRect();
      // Позиционируем слева от кнопки, под ней
      const left = fileBtnRect.left;
      const top = fileBtnRect.bottom + 12; // 12px отступ под кнопкой
      
      this.hoverPreview.style.left = `${left}px`;
      this.hoverPreview.style.top = `${top}px`;
    } else {
      // Fallback: если кнопка не найдена, позиционируем слева сверху
      this.hoverPreview.style.left = '16px'; // top-4 = 16px
      this.hoverPreview.style.top = '80px';
    }
    
    // Устанавливаем название сразу
    this.hoverPreview.setAttribute('data-name', name);
    
    // Показываем элемент сразу, даже до загрузки изображения
    this.hoverPreview.style.backgroundImage = `url(${thumbnailUrl})`;
    this.hoverPreview.classList.add('show');
    
    // Загружаем изображение для обновления
    const img = new Image();
    img.onload = () => {
      if (this.hoverPreview) {
        this.hoverPreview.style.backgroundImage = `url(${thumbnailUrl})`;
      }
    };
    img.onerror = () => {
      // При ошибке оставляем как есть
    };
    img.src = thumbnailUrl;
  }

  private hideHoverPreview(): void {
    if (this.hoverPreview) {
      this.hoverPreview.classList.remove('show');
      // Убираем после завершения анимации
      setTimeout(() => {
        if (this.hoverPreview && !this.hoverPreview.classList.contains('show')) {
          this.hoverPreview.style.backgroundImage = '';
        }
      }, 150);
    }
  }

  // Инициализация Intersection Observer для lazy loading превьюшек
  private initIntersectionObserver(): void {
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const card = entry.target as HTMLElement;
            const thumbnailUrl = card.getAttribute('data-thumbnail');
            
            if (thumbnailUrl && card.classList.contains('lazy-thumbnail')) {
              // Загружаем изображение
              const img = new Image();
              img.onload = () => {
                card.style.backgroundImage = `url(${thumbnailUrl})`;
                card.classList.remove('lazy-thumbnail');
              };
              img.onerror = () => {
                // При ошибке показываем placeholder
                card.style.backgroundImage = 'none';
                card.classList.remove('lazy-thumbnail');
              };
              img.src = thumbnailUrl;
              
              // Перестаём наблюдать за этим элементом
              this.intersectionObserver?.unobserve(card);
            }
          }
        });
      }, {
        root: this.presetsContainer,
        rootMargin: '50px', // Начинаем загрузку за 50px до появления в viewport
        threshold: 0.01
      });
    }
  }

  private selectMaterial(materialId: string | null, name: string): void {
    this.currentMaterial = materialId;
  }

  // Метод для сброса превью к дефолтному виду
  private resetMaterialPreview(): void {
    const materialSphere = document
      .getElementById('material-preview')
      ?.querySelector('.material-sphere') as HTMLElement | null;
    if (materialSphere) {
      // Удаляем background-image полностью
      materialSphere.style.backgroundImage = '';
      materialSphere.style.removeProperty('background-image');
      materialSphere.classList.remove('has-material');
    }
  }

  // Публичный метод для обновления превью по сохраненному presetId (при загрузке проекта)
  private async updatePreviewByPresetId(presetId: string | null): Promise<void> {
    if (!presetId) {
      // Сброс превью к дефолтному виду
      this.resetMaterialPreview();
      return;
    }
    
    // ВАЖНО: Убеждаемся, что пресеты загружены перед обновлением превью
    await this.ensureLoaded();
    
    const preset = this.presetsById[presetId];
    if (preset) {
      this.updateMaterialPreview(preset);
    } else {
      // Если пресет не найден после загрузки, сбрасываем превью
      console.warn(`Preset ${presetId} not found after loading presets`);
      this.resetMaterialPreview();
    }
  }

  private updateMaterialPreview(preset: any): void {
    const materialPreview = document.getElementById('material-preview');
    const materialSphere = materialPreview?.querySelector('.material-sphere');
    
    if (materialSphere && materialPreview) {
      // Используем тот же thumbnail что и для карточки
      let thumbnailUrl = preset.thumbnail ? `/presets/${preset.thumbnail}` : null;
      
      if (!thumbnailUrl && preset.path) {
        const normalUrl = `/presets/${preset.path}normal.jpg`;
        thumbnailUrl = normalUrl;
      }
      
      if (thumbnailUrl) {
        (materialSphere as HTMLElement).style.backgroundImage = `url(${thumbnailUrl})`;
        materialSphere.classList.add('has-material');
      } else {
        // Если нет thumbnail, сбрасываем к дефолтному виду
        this.resetMaterialPreview();
      }
    }
  }


  public getCurrentMaterial(): string | null {
    return this.currentMaterial;
  }
}
